import React from "react";
import { Asterisk, GripVertical } from "lucide-react";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import {
  getFieldDisplayTypeIcon,
  getFieldDisplayTypeLabel,
  getDataSourceBadge
} from "./hooks";
import { APIFieldItem, SettingsFieldType } from "@/pages/settings/types";
import { FieldDisplayType } from "./types";
import { useTranslation } from "react-i18next";
import { DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import IconContainer from "@/components/ui/icon-container";
import { Edit, Hide } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

const TRANSLATION_KEY_TABS = "settings.deals";

interface Props {
  field: APIFieldItem;
  index: number;
  provided: DraggableProvided | null;
  snapshot: DraggableStateSnapshot | null;
  flattenedFields: APIFieldItem[];
  showWorkflowColumn: boolean;
  onEditField: (field: APIFieldItem) => void;
  handleDeleteClick: (fieldId: string, isUnlink?: boolean) => void;
  disableDragAndDrop?: boolean;
  isAnchored?: boolean;
  onRowClick?: (field: APIFieldItem) => void;
  hideDelete?: boolean;
  onToggleVisibility?: (fieldId: string) => void;
  hideFieldEnabled?: boolean;
}

export const FieldRow: React.FC<Props> = ({
  field,
  index,
  provided,
  snapshot,
  flattenedFields,
  showWorkflowColumn,
  onEditField,
  handleDeleteClick,
  disableDragAndDrop = false,
  isAnchored = false,
  onRowClick,
  hideDelete = false,
  onToggleVisibility,
  hideFieldEnabled = false
}) => {
  const { t } = useTranslation();

  const getFieldOptions = (field: APIFieldItem): DetailedViewOptions => {
    const options: DetailedViewOptions = {};
    const { canEditDefinition, canEditOptions, canDelete, canHide } =
      field.fieldManagement ?? {};
    const isButton = field.displayType === FieldDisplayType.BUTTON;

    // Fields whose definition is managed by the platform (system fields, but
    // also AI/SCO-filled, formula, etc.) can't be edited or deleted by the
    // user. Rather than dropping those entries we keep them listed but disabled
    // with an explanatory tooltip, so every row's menu stays consistent.
    const disabledOption = (tooltipKey: string) =>
      ({
        action: () => {},
        disabled: true,
        disabledTooltipText: t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.${tooltipKey}`
        )
      }) as const;

    if (!isButton) {
      if (canEditDefinition || canEditOptions) {
        options.edit = () => onEditField(field);
      } else if (hideFieldEnabled) {
        // Redesign: keep Edit listed-but-disabled instead of omitting it.
        options.edit = disabledOption("systemFieldEditTooltip");
      }
    }
    // Hide/Show toggles deal-page visibility (showInDetailedView). Insert it
    // between Edit and Delete so the menu order matches the design.
    if (hideFieldEnabled && canHide && onToggleVisibility) {
      const key = field.showInDetailedView ? "hideField" : "showField";
      options[key] = () => onToggleVisibility(field.id);
    }
    if (!hideDelete) {
      if (canDelete && field.fieldType !== SettingsFieldType.SYSTEM) {
        if (field.fieldType === SettingsFieldType.LINKED) {
          options.unlink = {
            action: () => handleDeleteClick(field.id, true),
            variant: "default" as const
          };
        } else {
          options.delete = {
            action: () => handleDeleteClick(field.id, false),
            variant: "destructive" as const
          };
        }
      } else if (hideFieldEnabled) {
        // Redesign: keep Delete listed-but-disabled instead of omitting it.
        options.delete = disabledOption("systemFieldDeleteTooltip");
      }
    }

    return options;
  };

  // Page Header fields can only be reordered — per the design annotation
  // ("Header fields can only be ordered") they expose no hide, edit, or
  // more-actions controls. Gated behind the hide-field flag together with the
  // rest of this redesign so legacy behaviour is unchanged when it is off.
  const suppressActions =
    hideFieldEnabled && field.fieldType === SettingsFieldType.HEADER;

  const fieldOptions = suppressActions ? {} : getFieldOptions(field);
  const hasFieldOptions = Object.keys(fieldOptions).length > 0;

  // A hidden field stays in the table but is greyed out, with an inline
  // eye-off icon to toggle it back on. Header fields are never hidden.
  const isHidden =
    !suppressActions && hideFieldEnabled && !field.showInDetailedView;
  const mutedClass = isHidden ? "opacity-50" : "";

  const showFieldButton = isHidden &&
    field.fieldManagement?.canHide &&
    onToggleVisibility && (
      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            className="h-6 w-6"
            data-test="show-field"
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility(field.id);
            }}
          >
            <Hide />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent enableMarkdown={false}>
          {t(`${TRANSLATION_KEY_TABS}.fieldManagement.actions.showField`)}
        </TooltipContent>
      </Tooltip>
    );

  // Inline edit affordance, shown alongside the more-actions menu and mirroring
  // the menu's Edit entry: enabled for editable fields, and shown disabled with
  // an explanatory tooltip for system-managed fields that can't be edited. The
  // span wrapper keeps the tooltip working while the button itself is disabled.
  const isFieldEditable =
    field.fieldManagement?.canEditDefinition ||
    field.fieldManagement?.canEditOptions;
  const showEditShortcut =
    hideFieldEnabled &&
    !suppressActions &&
    field.displayType !== FieldDisplayType.BUTTON;

  const editButton = showEditShortcut && (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex opacity-0 transition-opacity group-hover:opacity-100">
          <IconButton
            variant="iconNoBg"
            size="sm"
            iconColor="secondary"
            disabled={!isFieldEditable}
            className="h-6 w-6"
            data-test="edit-field"
            onClick={(e) => {
              e.stopPropagation();
              onEditField(field);
            }}
          >
            <Edit />
          </IconButton>
        </span>
      </TooltipTrigger>
      <TooltipContent enableMarkdown={false}>
        {isFieldEditable
          ? t(`${TRANSLATION_KEY_TABS}.fieldManagement.actions.edit`)
          : t(`${TRANSLATION_KEY_TABS}.fieldManagement.systemFieldEditTooltip`)}
      </TooltipContent>
    </Tooltip>
  );

  const moreMenu = hasFieldOptions && (
    <OptionsDropdownMenu
      options={fieldOptions}
      tPrefix={`${TRANSLATION_KEY_TABS}.fieldManagement.actions`}
      buttonVariant="iconNoBg"
      iconColor={hideFieldEnabled ? "secondary" : undefined}
      iconSize={hideFieldEnabled ? "sm" : undefined}
      className={cn(
        "h-6 w-6",
        // Revealed on row hover (and kept visible while the menu is open via
        // OptionsDropdownMenu's own isOpen handling).
        "opacity-0 transition-opacity group-hover:opacity-100"
      )}
      showIcons={hideFieldEnabled}
      tooltipText={
        hideFieldEnabled
          ? t(`${TRANSLATION_KEY_TABS}.fieldManagement.actions.more`)
          : undefined
      }
      triggerIcon={hideFieldEnabled ? "horizontal-ellipsis" : "ellipsis"}
    />
  );

  const dragProps =
    disableDragAndDrop || !provided
      ? {}
      : {
          ref: provided.innerRef,
          ...provided.draggableProps,
          style: provided.draggableProps.style
        };

  return (
    <div
      {...dragProps}
      className={cn(
        "flex items-center",
        "min-h-[44px] px-[6px] py-[10px]",
        "bg-surface-widget-card",
        !disableDragAndDrop &&
          !snapshot?.isDragging &&
          index !== flattenedFields.length - 1 &&
          "border-b border-border-border1",
        disableDragAndDrop &&
          index !== flattenedFields.length - 1 &&
          "border-b border-border-border1",
        "hover:bg-surface-hover-soft transition-colors duration-200",
        !disableDragAndDrop &&
          snapshot?.isDragging && [
            "border-t border-b border-border-border1",
            "bg-surface-focus-selected",
            "shadow-[0px_12px_24px_-4px_rgba(0,0,0,0.20),0px_0px_2px_-2px_rgba(0,0,0,0.18)]",
            "relative z-50"
          ],
        "group",
        onRowClick && "cursor-pointer"
      )}
      onClick={() => onRowClick?.(field)}
    >
      <div className="w-6 flex items-center">
        {!disableDragAndDrop && provided && !isAnchored && (
          <div
            {...provided.dragHandleProps}
            className={cn(
              "cursor-grab active:cursor-grabbing",
              "opacity-0 group-hover:opacity-100 transition-opacity"
            )}
          >
            <GripVertical className="h-4 w-4 text-secondary" />
          </div>
        )}
      </div>
      <div className="w-[234px] flex items-start py-1">
        <Text
          variant="body"
          className={cn(
            "break-words leading-tight",
            isHidden && "text-disabled"
          )}
        >
          {field.label}
        </Text>
      </div>
      <div className="w-[200px] flex items-center gap-2 ml-4">
        {getFieldDisplayTypeIcon(
          field.displayType,
          isHidden ? "disabled" : undefined
        )}
        <Text
          variant="body"
          className={cn("truncate", isHidden && "text-disabled")}
        >
          {getFieldDisplayTypeLabel(field.displayType, t)}
        </Text>
      </div>
      <div
        className={cn(
          "flex items-center ml-4",
          showWorkflowColumn ? "w-[150px]" : "flex-1 justify-between"
        )}
      >
        <div className={cn("flex items-center", mutedClass)}>
          {getDataSourceBadge(field.dataSource, t)}
        </div>
        {!showWorkflowColumn &&
          (hideFieldEnabled ? (
            // Each action gets a fixed-width slot so the eye / edit / more-
            // actions icons always occupy the same columns and line up across
            // rows, regardless of which of them render for a given field.
            <div className="flex items-center gap-2 pr-2">
              <div className="flex h-6 w-6 items-center justify-center">
                {showFieldButton}
              </div>
              <div className="flex h-6 w-6 items-center justify-center">
                {editButton}
              </div>
              <div className="flex h-6 w-6 items-center justify-center">
                {moreMenu}
              </div>
            </div>
          ) : (
            (showFieldButton || editButton || moreMenu) && (
              <div className="flex items-center gap-1">
                {showFieldButton}
                {editButton}
                {moreMenu}
              </div>
            )
          ))}
      </div>
      {showWorkflowColumn && (
        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center w-[calc(100%-4rem)] justify-center">
            {field.required && (
              <IconContainer size={"5"} color={"primary"}>
                <Asterisk />
              </IconContainer>
            )}
          </div>
          {hasFieldOptions && (
            <OptionsDropdownMenu
              options={fieldOptions}
              tPrefix={`${TRANSLATION_KEY_TABS}.fieldManagement.actions`}
              buttonVariant="iconNoBg"
              iconColor="secondary"
              iconSize="sm"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              showIcons={false}
              triggerIcon="horizontal-ellipsis"
            />
          )}
        </div>
      )}
    </div>
  );
};
